/**
 * «Elektron yashil olam» — mehmon (tizimga kirmagan) foydalanuvchining sevimlilari va savati.
 * Brauzerda (`localStorage`) saqlanadi; tizimga kirgandan so'ng mijoz kabinetida
 * serverdagi savatga ko'chiriladi (`?checkout=1`, `ClientMarketPage`).
 */
import { useSyncExternalStore } from "react";

const FAV_KEY = "sf_shop_favorites";
const CART_KEY = "sf_shop_cart";

export interface GuestCartLine {
  code: string;
  quantity: number;
}

type Listener = () => void;
const listeners = new Set<Listener>();

function read<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

/** `useSyncExternalStore` har safar bir xil havolani kutadi: qiymat o'zgarmaguncha keshdan beriladi. */
const cache = new Map<string, { raw: string | null; value: unknown }>();

function snapshot<T>(key: string, fallback: T): T {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(key);
  } catch {
    /* saqlash yopiq bo'lsa — bo'sh qiymat */
  }
  const hit = cache.get(key);
  if (hit && hit.raw === raw) return hit.value as T;
  const value = raw ? read<T>(key, fallback) : fallback;
  cache.set(key, { raw, value });
  return value;
}

function write(key: string, value: unknown) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ahamiyatsiz */
  }
  listeners.forEach((listener) => listener());
}

function subscribe(listener: Listener) {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key === FAV_KEY || event.key === CART_KEY) listener();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

const EMPTY_FAVS: string[] = [];
const EMPTY_CART: GuestCartLine[] = [];

/* ------------------------------------------------------------------ sevimlilar */

export function useFavorites() {
  const codes = useSyncExternalStore(subscribe, () => snapshot<string[]>(FAV_KEY, EMPTY_FAVS));
  return {
    codes,
    has: (code: string) => codes.includes(code),
    toggle: (code: string) => {
      const current = snapshot<string[]>(FAV_KEY, EMPTY_FAVS);
      write(FAV_KEY, current.includes(code) ? current.filter((c) => c !== code) : [...current, code]);
    },
  };
}

/* ------------------------------------------------------------------------ savat */

export function readGuestCart(): GuestCartLine[] {
  return snapshot<GuestCartLine[]>(CART_KEY, EMPTY_CART);
}

export function clearGuestCart() {
  write(CART_KEY, EMPTY_CART);
}

export function useGuestCart() {
  const lines = useSyncExternalStore(subscribe, () => snapshot<GuestCartLine[]>(CART_KEY, EMPTY_CART));
  return {
    lines,
    count: lines.length,
    quantityOf: (code: string) => lines.find((line) => line.code === code)?.quantity ?? 0,
    /** Miqdorni belgilaydi (0 yoki kam — qatorni olib tashlaydi). */
    set: (code: string, quantity: number) => {
      const current = readGuestCart().filter((line) => line.code !== code);
      write(CART_KEY, quantity > 0 ? [...current, { code, quantity }] : current);
    },
    remove: (code: string) => write(CART_KEY, readGuestCart().filter((line) => line.code !== code)),
    clear: clearGuestCart,
  };
}
